import React from 'react';
import { Building, Zap, Leaf, TrendingUp, Info } from 'lucide-react';
import { Header } from '../components/Layout/Header';
import { Card } from '../components/UI/Card';
import { Button } from '../components/UI/Button';

const tokenizedAssets = [
  {
    id: '1',
    name: 'Manhattan Office Building',
    type: 'real-estate',
    value: 2500000,
    tokens: 1000,
    apy: 8.5,
    description: 'Prime commercial real estate in Manhattan',
    image: 'https://images.pexels.com/photos/2462015/pexels-photo-2462015.jpeg?auto=compress&cs=tinysrgb&w=400',
  },
  {
    id: '2',
    name: 'Solar Farm Texas',
    type: 'energy',
    value: 1200000,
    tokens: 500,
    apy: 12.3,
    description: 'Renewable energy solar farm project',
    image: 'https://images.pexels.com/photos/9875414/pexels-photo-9875414.jpeg?auto=compress&cs=tinysrgb&w=400',
  },
  {
    id: '3',
    name: 'Carbon Credits Portfolio',
    type: 'carbon',
    value: 850000,
    tokens: 250,
    apy: 6.8,
    description: 'Verified carbon offset credits',
    image: 'https://images.pexels.com/photos/1108572/pexels-photo-1108572.jpeg?auto=compress&cs=tinysrgb&w=400',
  },
];

const getAssetIcon = (type: string) => {
  switch (type) {
    case 'real-estate':
      return Building;
    case 'energy':
      return Zap;
    case 'carbon':
      return Leaf;
    default:
      return Building;
  }
};

const getAssetColor = (type: string) => {
  switch (type) {
    case 'real-estate':
      return 'bg-blue-100 text-blue-600';
    case 'energy':
      return 'bg-yellow-100 text-yellow-600';
    case 'carbon':
      return 'bg-green-100 text-green-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

export const Assets: React.FC = () => {
  const totalValue = tokenizedAssets.reduce((sum, asset) => sum + asset.value, 0);
  const totalTokens = tokenizedAssets.reduce((sum, asset) => sum + asset.tokens, 0);

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Header title="Tokenized Assets" showProfile={false} />
      
      {/* Portfolio Overview */}
      <div className="p-4">
        <Card className="bg-gradient-to-br from-primary-600 to-primary-700 text-white mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary-100 text-sm">Total Portfolio Value</p>
              <h2 className="text-3xl font-bold">${(totalValue / 1000000).toFixed(1)}M</h2>
              <p className="text-primary-100 text-sm">{totalTokens} tokens owned</p>
            </div>
            <div className="text-right">
              <div className="flex items-center text-green-300">
                <TrendingUp size={16} className="mr-1" />
                <span className="text-sm">+18.5%</span>
              </div>
              <p className="text-primary-100 text-xs">30d performance</p>
            </div>
          </div>
        </Card>

        {/* Asset Categories */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card className="text-center">
            <Building size={24} className="text-blue-600 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900">Real Estate</p>
            <p className="text-xs text-gray-500">1,000 tokens</p>
          </Card>
          <Card className="text-center">
            <Zap size={24} className="text-yellow-600 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900">Energy</p>
            <p className="text-xs text-gray-500">500 tokens</p>
          </Card>
          <Card className="text-center">
            <Leaf size={24} className="text-green-600 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900">Carbon</p>
            <p className="text-xs text-gray-500">250 tokens</p>
          </Card>
        </div>
      </div>

      {/* Asset List */}
      <div className="px-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Your Assets</h3>
          <Button variant="ghost" size="sm">
            <Info size={16} className="mr-1" />
            Learn More
          </Button>
        </div>
        
        <div className="space-y-4">
          {tokenizedAssets.map((asset) => {
            const Icon = getAssetIcon(asset.type);
            const colorClass = getAssetColor(asset.type);
            
            return (
              <Card key={asset.id} hover>
                <div className="flex items-start space-x-4">
                  <img
                    src={asset.image}
                    alt={asset.name}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-900">{asset.name}</h4>
                        <p className="text-sm text-gray-500">{asset.description}</p>
                      </div>
                      <div className={`p-2 rounded-full ${colorClass}`}>
                        <Icon size={16} />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex space-x-4">
                        <div>
                          <p className="text-xs text-gray-500">Tokens Owned</p>
                          <p className="font-semibold text-gray-900">{asset.tokens}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">APY</p>
                          <p className="font-semibold text-green-600">{asset.apy}%</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Value</p>
                        <p className="font-semibold text-gray-900">
                          ${(asset.value / 1000000).toFixed(1)}M
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Investment Opportunities */}
      <div className="px-4 mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">New Opportunities</h3>
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-gray-900">Miami Beach Resort</h4>
              <p className="text-sm text-gray-600">Luxury beachfront property tokenization</p>
              <div className="flex items-center space-x-4 mt-2">
                <span className="text-sm text-gray-500">Min: $1,000</span>
                <span className="text-sm text-green-600 font-medium">Est. APY: 10.2%</span>
              </div>
            </div>
            <Button size="sm">
              Invest
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};